/** Explicit staff param for "Alle medarbejdere" (every active member). */
export const APPOINTMENTS_STAFF_ALL_EMPLOYEES = 'employees';

const STAFF_STORAGE_PREFIX = 'appointments-staff';

export function appointmentsStaffStorageKey(userId: string, clinicId: string): string {
  return `${STAFF_STORAGE_PREFIX}:${userId}:${clinicId}`;
}

export function readRememberedAppointmentsStaff(
  userId: string | null | undefined,
  clinicId: string | null | undefined,
): string | null {
  if (!userId || !clinicId) return null;
  try {
    const raw = localStorage.getItem(appointmentsStaffStorageKey(userId, clinicId));
    const value = raw?.trim();
    return value ? value : null;
  } catch {
    return null;
  }
}

export function writeRememberedAppointmentsStaff(
  userId: string | null | undefined,
  clinicId: string | null | undefined,
  staff: string,
): void {
  if (!userId || !clinicId || !staff) return;
  try {
    localStorage.setItem(appointmentsStaffStorageKey(userId, clinicId), staff);
  } catch {
    // Private mode and blocked storage leave the URL as the only copy.
  }
}

/**
 * A remembered choice is used only when the URL has no staff param.
 * `all` and `employees` stay valid. A person who is gone, or who is no longer
 * a practitioner once roles are known, is dropped so the caller can use the
 * logged-in default.
 */
export function usableRememberedAppointmentsStaff(input: {
  remembered: string | null;
  memberIds: readonly string[] | null;
  practitionerIds: readonly string[] | null;
}): string | null {
  const remembered = input.remembered?.trim() ?? '';
  if (!remembered) return null;
  if (remembered === 'all' || remembered === APPOINTMENTS_STAFF_ALL_EMPLOYEES) {
    return remembered;
  }
  if (input.memberIds == null) return remembered;
  if (!input.memberIds.includes(remembered)) return null;
  if (input.practitionerIds != null && !input.practitionerIds.includes(remembered)) return null;
  return remembered;
}

export function isAppointmentsAllEmployees(staffParam: string | null | undefined): boolean {
  return staffParam === APPOINTMENTS_STAFF_ALL_EMPLOYEES;
}

/**
 * Resolves the /appointments time-view clinician filter.
 *
 * An explicit `staff` query param always wins. When it is absent, the logged-in
 * user is selected if they appear in the clinic staff list; otherwise the
 * existing All-staff default is kept.
 *
 * `memberIds` is null while clinic members are still loading so the logged-in
 * user can be used optimistically and then confirmed (or cleared) once the
 * list arrives.
 */
export function resolveAppointmentsStaffFilter(input: {
  staffParam: string | null;
  hasExplicitStaffParam: boolean;
  currentUserId: string | null | undefined;
  memberIds: readonly string[] | null;
}): string | null {
  const { staffParam, hasExplicitStaffParam, currentUserId, memberIds } = input;

  if (hasExplicitStaffParam) {
    if (!staffParam || staffParam === 'all' || isAppointmentsAllEmployees(staffParam)) return null;
    if (staffParam.includes(',')) return null;
    if (memberIds == null) return staffParam;
    if (memberIds.includes(staffParam)) return staffParam;
    return defaultStaffFilter(currentUserId, memberIds);
  }

  if (memberIds == null) {
    return currentUserId ?? null;
  }

  return defaultStaffFilter(currentUserId, memberIds);
}

function defaultStaffFilter(
  currentUserId: string | null | undefined,
  memberIds: readonly string[],
): string | null {
  if (currentUserId && memberIds.includes(currentUserId)) {
    return currentUserId;
  }
  return null;
}

export function staffSearchValueFromSelection(clinicianId: string | null): string {
  return clinicianId ?? 'all';
}
